
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { MapPin, Clock, Users, PhoneCall } from 'lucide-react';
import { Button } from "@/components/ui/button";
import PageHeader from '@/components/PageHeader';
import LogoScroller from '@/components/LogoScroller';

const Venue = () => {
  return (
    <div className="min-h-screen bg-ai-dark">
      <Helmet>
        <title>Venue - AI World Congress 2026 - November 25-26, London UK - AI Conference</title>
        <meta name="description" content="AI World Congress 2026 venue - The Great Hall, Kensington Conference Centre, London W8 7NX. November 25-26, 2026. Transportation, accommodations and venue info." />
        <link rel="canonical" href="https://aiconference.london/venue" />
        <meta property="og:title" content="Venue - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta property="og:description" content="Join us at The Great Hall, Kensington for AI World Congress 2026. November 25-26, London." />
        <meta property="og:url" content="https://aiconference.london/venue" />
        <meta property="og:image" content="https://aiconference.london/lovable-uploads/og-banner-2026.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Venue - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta name="twitter:description" content="The Great Hall, Kensington - November 25-26, 2026" />
        <meta name="twitter:image" content="https://aiconference.london/lovable-uploads/og-banner-2026.jpg" />
      </Helmet>
      <Header />
      <PageHeader
        title=""
        subtitle=""
        backgroundImage="/lovable-uploads/530fddbf-4fea-4198-860f-8165d2f8b445.png"
        className="bg-gradient-to-b from-purple-900 to-black"
      />
      <div className="relative z-10 -mt-16">
        <div className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-purple-900/80 to-black">
          <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
            <div className="absolute w-full h-[40%] bottom-0 bg-gradient-to-t from-black to-transparent"></div>
            <div className="absolute w-full h-[500px] bottom-0 left-0 right-0">
              <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg">
                <path 
                  fill="#000000" 
                  fillOpacity="1" 
                  d="M0,128L48,138.7C96,149,192,171,288,176C384,181,480,171,576,144C672,117,768,75,864,80C960,85,1056,139,1152,160C1248,181,1344,171,1392,165.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                  className="animate-pulse"
                  style={{ animationDuration: '15s' }}
                ></path>
              </svg>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold mb-8">
                <span className="text-white">Conference </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Venue</span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Join us at The Great Hall for two days of immersive AI experiences and networking
              </p>
            </div>
          </div>
        </div>

        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <Link to="/tickets">
                  <Button 
                    className="mb-12 text-lg px-8 py-6 bg-gradient-to-r from-[#F97316] to-[#ea384c] hover:opacity-90 text-white font-semibold shadow-lg"
                  >
                    Register Now
                  </Button>
                </Link>
                <h2 className="text-3xl font-bold text-white mb-6">About the Venue</h2>
                <p className="text-gray-300 mb-6">
                  Join us at The Great Hall in the Kensington Conference and Events Centre for AI World Congress 2026. 
                  Located in the heart of London, this prestigious venue offers the perfect setting for our two-day 
                  conference on November 25-26, 2026.
                </p>
                
                <div className="space-y-6 mt-8">
                  <div className="flex items-start space-x-4">
                    <MapPin className="text-purple-500 h-6 w-6 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-white">Location</h3>
                      <p className="text-gray-300">The Great Hall</p>
                      <p className="text-gray-300">Kensington Conference and Events Centre</p>
                      <p className="text-gray-300">Hornton St, Kensington</p>
                      <p className="text-gray-300">London W8 7NX</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <Clock className="text-purple-500 h-6 w-6 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-white">Event Hours</h3>
                      <p className="text-gray-300">November 25-26, 2026 | 9:00 AM - 5:00 PM daily</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <PhoneCall className="text-purple-500 h-6 w-6 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-white">Contact</h3>
                      <p className="text-gray-300">Email: info@aiconference.london</p>
                      <p className="text-gray-300">Tel: +442037735519</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-8">
                <div className="rounded-lg overflow-hidden h-64 w-full">
                  <iframe 
                    title="Venue Location" 
                    className="w-full h-full border-0"
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d8239.90262729275!2d-0.194682!3d51.501974!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760ff0d9eb2ae3%3A0x5e7e132e7b81589!2sKensington%20Conference%20and%20Event%20Centre!5e1!3m2!1sen!2suk!4v1745485702198!5m2!1sen!2suk" 
                    width="600" 
                    height="450" 
                    style={{ border: 0 }}
                    allowFullScreen 
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                
                <div className="bg-gray-900 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-white mb-4">Nearby Accommodations</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Safestay London Kensington Holland Park</span>
                        <span className="text-purple-400">£32</span>
                      </div>
                      <div className="text-sm text-gray-400">3.5(1.8K) · 4-star hotel</div>
                    </li>
                    <li>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Holiday Inn London - Kensington High St. by IHG</span>
                        <span className="text-purple-400">£183</span>
                      </div>
                      <div className="text-sm text-gray-400">3.8(4.8K) · 4-star hotel</div>
                      <div className="text-xs text-gray-500 mt-1">Casual rooms, plus dining & a spa</div>
                    </li>
                    <li>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Copthorne Tara Hotel London Kensington</span>
                        <span className="text-purple-400">£207</span>
                      </div>
                      <div className="text-sm text-gray-400">3.8(7.8K) · 4-star hotel</div>
                      <div className="text-xs text-gray-500 mt-1">Polished lodging with restaurants & bars</div>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gray-900 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-white mb-4">Transportation</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li>• Tube: High Street Kensington</li>
                    <li>• Bus: TFL London Bus Map: <a href="https://tfl.gov.uk/maps/bus" className="text-purple-400 underline hover:text-purple-300">https://tfl.gov.uk/maps/bus</a></li>
                    <li>• Taxi: Uber</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <LogoScroller />
      <Footer />
    </div>
  );
};

export default Venue;
